import { BathProductPage, BathProductConfig } from "@/components/bath/BathProductPage";
import { Rem7HydraSpecPDFButton } from "@/components/bath/Rem7HydraSpecPDFButton";
import { Rem7HydraSpecsPanel, SpecGroup, DimensionMetric, InBoxItem } from "@/components/bath/Rem7HydraSpecsPanel";
import { Rem7HydraFeatureShowcase, FeatureModule } from "@/components/bath/Rem7HydraFeatureShowcase";
import {
  Sparkles, ShieldCheck, Wrench, Layers, TrendingUp, CheckCircle2,
  Home, Building2, Hotel,
  Ruler, Waves, Radio, Droplets, Cog, Lightbulb, Settings2, Speaker,
  ShowerHead, Armchair, Package, Square,
} from "lucide-react";

/**
 * /:lang/bath-sanitary/rem7-whirlpool-compact
 * REM7 Hydra Compact — 1800mm acrylic whirlpool with 1HP hydro pump,
 * chromotherapy LED, digital touch control, FM radio and ozone disinfection.
 * Compact single-bather variant of the REM7 Hydra spa programme.
 */
const config: BathProductConfig = {
  slug: "rem7-whirlpool-compact",
  category: "Whirlpool Massage Bathtub",
  productName: "REM7 Hydra Compact Acrylic Whirlpool Massage Bathtub",
  shortName: "REM7 Hydra Compact",
  sku: "REM7-HYDRA-1826",
  schemaCategory: "Bathtubs > Whirlpool > Acrylic",
  metaTitle: "REM7 Hydra Compact | 1800mm Acrylic Whirlpool Tub",
  metaDescription:
    "REM7 Hydra Compact — 1800×1100×650mm acrylic whirlpool with 1HP hydro pump, chromotherapy LED and digital control. CE certified, EU bulk supply.",
  heroEyebrow: "Project Solutions · Compact Whirlpool Spa Bathtub",
  heroHeadline: (
    <>
      Compact Acrylic Whirlpool{" "}
      <span className="bg-gradient-to-r from-amber-300 via-amber-400 to-amber-200 bg-clip-text text-transparent">
        Massage Bathtub
      </span>
    </>
  ),
  heroIntro:
    "The compact 1800 mm sibling of the REM7 Hydra. Hydro and air massage, chromotherapy LED, digital touch control with FM radio and ozone disinfection — engineered for tighter primary suites, boutique hotel rooms and apartment bathrooms where space is at a premium.",
  premiumSummary:
    "An 1800 × 1100 × 650 mm single-bather whirlpool bathtub built around 100% pure sanitary-grade PMMA acrylic, a 1HP water massage pump, 16-jet ultra-thin hydro array, 300W bubble pump and a 90° 8 mm decorative acrylic apron. Hospitality-grade specification at compact-format trade pricing.",
  galleryImages: [
    {
      src: "/images/bath/whirlpool-compact/hero.jpg",
      alt: "REM7 Hydra Compact 1800mm acrylic whirlpool massage bathtub with chromotherapy LED",
      title: "REM7 Hydra Compact Whirlpool — Hero",
      caption: "Hero · Compact whirlpool with LED",
      description:
        "Hero of the REM7 Hydra Compact 1800 mm whirlpool massage bathtub in white acrylic with chromotherapy LED bubble jets, sized for tighter European bathroom layouts.",
    },
    {
      src: "/images/bath/whirlpool-compact/angle.jpg",
      alt: "REM7 Hydra Compact whirlpool bathtub three-quarter angle view",
      title: "REM7 Hydra Compact — Angle View",
      caption: "Angle · Single-bather format",
      description:
        "Three-quarter angle of the REM7 Hydra Compact whirlpool bathtub showing the 90° decorative acrylic apron and integrated stainless steel armrest.",
    },
    {
      src: "/images/bath/whirlpool-compact/controls.jpg",
      alt: "REM7 Hydra Compact digital touch control panel ozone disinfection FM radio",
      title: "REM7 Hydra Compact — Digital Control",
      caption: "Detail · Digital touch control",
      description:
        "Macro of the REM7 Hydra Compact digital touch control system with water sensor, ozone disinfection, FM radio and integrated loudspeaker.",
    },
    {
      src: "/images/bath/whirlpool-compact/jets.jpg",
      alt: "REM7 Hydra Compact ultra-thin hydro massage jets 4 large 12 small",
      title: "REM7 Hydra Compact — Hydro Jets",
      caption: "Detail · Ultra-thin hydro jets",
      description:
        "Detail of the ultra-thin hydro jet array on the REM7 Hydra Compact — 4 large + 12 small water jets driven by a 1HP water massage pump.",
    },
    {
      src: "/images/bath/whirlpool-compact/led.jpg",
      alt: "REM7 Hydra Compact chromotherapy LED bubble jets underwater lighting",
      title: "REM7 Hydra Compact — Chromotherapy LED",
      caption: "Lighting · LED chromotherapy",
      description:
        "Underwater chromotherapy on the REM7 Hydra Compact — 16 colourful LED bubble jets driven by a 300W air pump for spa-grade ambience.",
    },
    {
      src: "/images/bath/whirlpool-compact/detail.jpg",
      alt: "REM7 Hydra Compact whirlpool detail decorative acrylic apron",
      title: "REM7 Hydra Compact — Apron Detail",
      caption: "Detail · 90° decorative apron",
      description:
        "Close-up of the REM7 Hydra Compact 90° 8 mm decorative transparent acrylic apron and 2-side 1-pc integral skirt.",
    },
    {
      src: "/images/bath/whirlpool-compact/lifestyle.jpg",
      alt: "REM7 Hydra Compact whirlpool installed in luxury hotel suite",
      title: "REM7 Hydra Compact — Hotel Lifestyle",
      caption: "Lifestyle · Boutique hotel suite",
      description:
        "Lifestyle scene: REM7 Hydra Compact whirlpool installed in a boutique hotel suite with hot/cold mixer, diverter and hand shower.",
    },
    {
      src: "/images/bath/whirlpool-compact/side.jpg",
      alt: "REM7 Hydra Compact whirlpool side profile",
      title: "REM7 Hydra Compact — Side Profile",
      caption: "Studio · Side profile",
      description:
        "Side studio profile of the REM7 Hydra Compact whirlpool bathtub showing the 2-side 1-pc integral apron silhouette.",
    },
  ],
  specs: [
    { label: "Dimensions", value: "1800 × 1100 × 650 mm (L × W × H)" },
    { label: "Capacity", value: "Single bather · ≈ 320 L to overflow" },
    { label: "Shell Material", value: "100% Sanitary-grade PMMA Acrylic" },
    { label: "Reinforcement", value: "Alkali-free fibreglass + resin + wood reinforcement" },
    { label: "Hydro Massage", value: "1HP pump · 4 large + 12 small ultra-thin jets" },
    { label: "Air / Bubble Bath", value: "300W bubble pump · 16 colourful LED bubble jets" },
    { label: "Control System", value: "Digital touch · water sensor · ozone · FM radio · loudspeaker" },
    { label: "Plumbing", value: "Hot/cold mixer · diverter · hand shower · drainer with overflow" },
    { label: "Apron", value: "2-side 1-pc integral apron · 90° 8 mm decorative acrylic" },
    { label: "Pillows", value: "2 × dark grey ergonomic headrests" },
    { label: "Support / Feet", value: "Stainless steel frame · pre-levelled height-adjustable feet" },
    { label: "Certification", value: "CE · SGS · cUPC · ISO 9001" },
    { label: "Warranty", value: "5 years shell · 2 years components" },
    { label: "MOQ", value: "From 5 units · Bulk pricing 20+" },
    { label: "Lead Time", value: "20–30 working days · 40ft container ≈ 32 units" },
  ],
  quickFeatures: [
    "1800 × 1100 × 650 mm compact single-bather format",
    "1HP hydro pump · 16 ultra-thin water jets",
    "300W air pump · 16 chromotherapy LED bubble jets",
    "Digital touch control with FM radio & loudspeaker",
    "Ozone disinfection + water-sensor pump protection",
    "90° 8 mm decorative acrylic apron",
    "100% sanitary-grade PMMA acrylic shell",
    "CE / SGS / cUPC · 5-yr shell warranty",
  ],
  productMaterial: "100% Sanitary-grade PMMA Acrylic with fibreglass + resin reinforcement",
  additionalProperties: [
    { name: "Length", value: "1800", unitText: "MMT" },
    { name: "Width", value: "1100", unitText: "MMT" },
    { name: "Height", value: "650", unitText: "MMT" },
    { name: "Capacity", value: "320", unitText: "LTR" },
    { name: "Hydro Pump Power", value: "1", unitText: "HP" },
    { name: "Air Pump Power", value: "300", unitText: "WTT" },
    { name: "Voltage", value: "220V / 50Hz" },
    { name: "Bather Capacity", value: "1" },
    { name: "Hydro Jets", value: "16 (4 large + 12 small)" },
    { name: "LED Bubble Jets", value: "16" },
    { name: "Certification", value: "CE, SGS, cUPC, ISO 9001" },
    { name: "Shell Warranty", value: "5 years" },
    { name: "Component Warranty", value: "2 years" },
  ],
  whyChoose: [
    {
      icon: Sparkles,
      title: "Full spa specification, compact footprint",
      body: "Hydro massage, air bubble massage, chromotherapy LED, digital touch control, FM radio and ozone — packaged into an 1800 mm footprint that fits standard European bathroom rough-ins.",
    },
    {
      icon: TrendingUp,
      title: "Higher ADR per hotel suite",
      body: "A whirlpool tub photographs as a destination feature even in compact rooms. Boutique hotels and short-let operators justify higher nightly rates on whirlpool-equipped suites.",
    },
    {
      icon: ShieldCheck,
      title: "Sanitary-grade durability",
      body: "100% pure PMMA acrylic, fibreglass + resin + wood reinforcement, water-sensor protected pump, and stainless support. CE / SGS / cUPC certified for residential and hospitality.",
    },
    {
      icon: Wrench,
      title: "Specifier-friendly installation",
      body: "Standard EU plumbing rough-in, integrated drainer with overflow, and pre-levelled adjustable feet. 2-side 1-pc integral apron simplifies finish detailing on site.",
    },
    {
      icon: Layers,
      title: "Better container density",
      body: "Compact format ships ~32 units per 40ft container vs ~28 for the full-size REM7 Hydra — improving freight efficiency on phased developer rollouts.",
    },
    {
      icon: CheckCircle2,
      title: "Margin-protective pricing",
      body: "Trade pricing structured to protect project margin — hospitality-grade whirlpool aesthetic without the European brand premium.",
    },
  ],
  perfectFor: [
    { icon: Hotel, title: "Boutique Hotels", body: "Compact suites and standard rooms where a whirlpool is the upgrade story." },
    { icon: Home, title: "Apartments", body: "Branded residences and high-end rentals where bathroom space is constrained." },
    { icon: Building2, title: "Serviced Apartments", body: "Short-let inventory needing a photographable spa amenity in a tighter footprint." },
    { icon: Wrench, title: "Bathroom Renovations", body: "Refurbishments upgrading from a standard 1700–1800 mm tub to a true whirlpool." },
  ],
  faqs: [
    {
      question: "How does the Compact differ from the full-size REM7 Hydra whirlpool?",
      answer:
        "The Compact is the 1800 × 1100 × 650 mm single-bather variant of the REM7 Hydra programme. Same digital control, ozone, chromotherapy LED and 16-jet hydro array — but with a 1HP pump (vs 1.5HP) and a tighter footprint suited to standard European bathrooms.",
    },
    {
      question: "What are the dimensions and capacity?",
      answer:
        "The REM7 Hydra Compact measures 1800 × 1100 × 650 mm (L × W × H) with a working capacity of approximately 320 litres to overflow. It is sized as a single-bather whirlpool, ideal for standard primary bathrooms and boutique hotel rooms.",
    },
    {
      question: "Is the REM7 Hydra Compact certified for European installation?",
      answer:
        "Yes. It carries CE, SGS, cUPC and ISO 9001 certification, and is engineered around standard EU plumbing rough-in with hot/cold mixer, diverter, hand shower and integrated drainer with overflow.",
    },
    {
      question: "What massage system is included?",
      answer:
        "A dual hydro + air system: a 1HP water pump driving 16 ultra-thin hydro jets (4 large + 12 small), and a separate 300W air bubble pump powering 16 chromotherapy LED bubble jets — controlled independently from the digital touch panel.",
    },
    {
      question: "What is the warranty on the REM7 Hydra Compact?",
      answer:
        "Aureum VII Trading covers it with a 5-year shell warranty and 2-year warranty on electrical and mechanical components (pumps, control unit, LED). Professional installation is required for warranty validity.",
    },
    {
      question: "What is the MOQ and how many units fit a container?",
      answer:
        "MOQ starts at 5 units, with bulk pricing tiers from 20 units. Production lead time is 20–30 working days. The compact format ships approximately 32 units per 40ft container — better freight density than the full-size REM7 Hydra.",
    },
    {
      question: "Does it include hygiene and safety features?",
      answer:
        "Yes. Ozone disinfection sanitises water and pipework between uses, a water-sensor circuit prevents the hydro pump from running dry, and pre-levelled stainless adjustable feet plus sealed electronics meet hospitality safety expectations.",
    },
    {
      question: "Can it be specified alongside the full-size REM7 Hydra in the same project?",
      answer:
        "Yes — that is a common specification pattern. Developers and hoteliers use the full-size 1850 mm REM7 Hydra in primary/master suites and the 1800 mm Compact in standard rooms, keeping a coherent spa programme across the room category.",
    },
  ],
  longDescription: [
    "The REM7 Hydra Compact is the 1800 mm single-bather variant of the REM7 Hydra whirlpool programme — engineered for the tighter primary bathrooms, boutique hotel rooms and serviced apartment formats that dominate European urban projects.",
    "It carries the same hospitality-grade dual hydro + air massage system, digital touch control with chromotherapy LED, FM radio and ozone disinfection — packaged into a smaller 1800 × 1100 × 650 mm footprint with a 1HP hydro pump (vs 1.5HP on the full-size unit) and a 320 L working capacity.",
    "The shell is cast from 100% sanitary-grade PMMA acrylic, reinforced with alkali-free fibreglass, resin and structural wood. A water-sensor circuit protects the pump from running dry, and an ozone disinfection cycle keeps the water and pipework hygienic between guests.",
    "For developers and hotel operators, the Compact unlocks whirlpool specification in room categories where the full-size REM7 Hydra simply does not fit. ~32 units per 40ft container also improves freight density on phased rollouts. Specifier-friendly installation: standard EU plumbing rough-in, integrated drainer with overflow, pre-levelled stainless adjustable feet, and a 2-side 1-pc integral apron with a 90° 8 mm decorative acrylic accent.",
    "Specify alongside the full-size REM7 Hydra (master suites), the ARIA Sculpt / Monolith / Diamond bathtubs (signature freestanding rooms), the VENA basin and vanity range, and the REM7 smart toilet flagship — a coherent Aureum VII bath programme for branded residences, boutique hotels and signature private villas across Europe.",
  ],
};

const dimensionMetrics: DimensionMetric[] = [
  { label: "Length", value: "1800", unit: "mm" },
  { label: "Width", value: "1100", unit: "mm" },
  { label: "Height", value: "650", unit: "mm" },
  { label: "Capacity", value: "320", unit: "Litres" },
];

const specGroups: SpecGroup[] = [
  {
    id: "dimensions",
    icon: Ruler,
    title: "Dimensions & Format",
    rows: [
      { label: "Overall Size", value: "1800 × 1100 × 650 mm" },
      { label: "Format", value: "Rectangular · single bather" },
      { label: "Capacity", value: "≈ 320 L to overflow" },
      { label: "Apron", value: "2-side 1-pc integral · 90° 8 mm acrylic" },
      { label: "Net Weight (approx.)", value: "≈ 105 kg" },
    ],
  },
  {
    id: "hydromassage",
    icon: Waves,
    title: "Hydro Massage System",
    rows: [
      { label: "Hydro Pump", value: "1 HP water massage pump" },
      { label: "Hydro Jets", value: "16 ultra-thin (4 large + 12 small)" },
      { label: "Pump Protection", value: "Water-sensor cut-off circuit" },
      { label: "Massage Coverage", value: "Back · lumbar · legs" },
    ],
  },
  {
    id: "bubble",
    icon: Sparkles,
    title: "Air Bubble & LED",
    rows: [
      { label: "Air Pump", value: "300 W bubble pump" },
      { label: "LED Bubble Jets", value: "16 chromotherapy LEDs" },
      { label: "Operating Mode", value: "Independent of hydro circuit" },
      { label: "Lighting", value: "Multi-colour chromotherapy" },
    ],
  },
  {
    id: "control",
    icon: Radio,
    title: "Digital Touch Control",
    rows: [
      { label: "Interface", value: "Digital touch panel" },
      { label: "Water Sensor", value: "Yes · safe-operation lockout" },
      { label: "Disinfection", value: "Ozone module on recirculation" },
      { label: "Audio", value: "FM radio + integrated loudspeaker" },
      { label: "Power Supply", value: "220 V / 50 Hz" },
    ],
  },
  {
    id: "plumbing",
    icon: Droplets,
    title: "Plumbing & Hardware",
    rows: [
      { label: "Mixer", value: "Hot/cold mixer + diverter mixer" },
      { label: "Hand Shower", value: "Included with hose" },
      { label: "Drain", value: "Backwater drainer with overflow" },
      { label: "Pillows", value: "2 × dark grey ergonomic headrests" },
    ],
  },
  {
    id: "build",
    icon: ShieldCheck,
    title: "Build, Certification & Logistics",
    rows: [
      { label: "Shell Material", value: "100% sanitary-grade PMMA acrylic" },
      { label: "Reinforcement", value: "Fibreglass + resin + wood" },
      { label: "Support Frame", value: "Stainless steel · adjustable feet" },
      { label: "Certification", value: "CE · SGS · cUPC · ISO 9001" },
      { label: "Warranty", value: "5 yrs shell · 2 yrs components" },
      { label: "MOQ / Container", value: "From 5 units · ≈ 32 / 40ft" },
      { label: "Lead Time", value: "20–30 working days" },
    ],
  },
];

const inBoxItems: InBoxItem[] = [
  { icon: Settings2, label: "Digital touch control panel", qty: "1 unit · pre-installed" },
  { icon: Cog, label: "1 HP whirlpool pump", qty: "1 unit · with run-dry guard" },
  { icon: Waves, label: "Ultra-thin hydro jets", qty: "16 jets (4L + 12S)" },
  { icon: Sparkles, label: "300 W air bubble pump", qty: "1 unit" },
  { icon: Lightbulb, label: "Chromotherapy LED bubble jets", qty: "16 LEDs" },
  { icon: ShowerHead, label: "Hot/cold mixer + diverter", qty: "1 set + hand shower" },
  { icon: Package, label: "Backwater drainer + overflow", qty: "1 set · pre-fitted" },
  { icon: Armchair, label: "Ergonomic headrest pillows", qty: "2 units · dark grey" },
  { icon: Square, label: "Decorative acrylic apron", qty: "1 × 90° 8 mm panel" },
  { icon: Wrench, label: "Stainless steel support frame", qty: "1 set · adjustable feet" },
  { icon: Speaker, label: "FM radio + loudspeaker module", qty: "1 unit · integrated" },
  { icon: Package, label: "Installation & warranty pack", qty: "1 set · CE certificate" },
];

const featureModules: FeatureModule[] = [
  {
    id: "control",
    icon: Radio,
    label: "Touch Console",
    tagline: "Module 01 · Control",
    title: "Digital touch console with ozone, FM radio & loudspeaker",
    description:
      "A single digital touch panel runs every system on the Compact — whirlpool, air bubble and lighting — with built-in water sensor, ozone disinfection, FM radio and loudspeaker for an integrated spa-audio experience in a smaller footprint.",
    bullets: [
      "Digital touch interface · ultra-thin glass surface",
      "Water sensor for safe operation and dry-run protection",
      "Ozone disinfection module on the recirculation loop",
      "Built-in FM radio + loudspeaker for in-tub audio",
    ],
    image: { src: "/images/bath/whirlpool-compact/controls.jpg", alt: "REM7 Hydra Compact digital touch control panel" },
    metric: { value: "1-Touch", label: "Unified Spa Control" },
  },
  {
    id: "hydro",
    icon: Waves,
    label: "Hydro Massage",
    tagline: "Module 02 · Hydro Massage",
    title: "1HP hydro massage with 16 ultra-thin jets",
    description:
      "A 1HP whirlpool pump drives a dual-zone hydro massage through 4 large + 12 small ultra-thin jets, configured for back, lumbar and leg coverage. Water-sensor cut-off protects the pump from running dry.",
    bullets: [
      "1HP whirlpool pump · water-sensor cut-off circuit",
      "16 ultra-thin water jets (4 large + 12 small)",
      "Adjustable air whirlpool switch for massage intensity",
      "Single-bather format · back / lumbar / leg coverage",
    ],
    image: { src: "/images/bath/whirlpool-compact/jets.jpg", alt: "REM7 Hydra Compact hydro massage jet array" },
    metric: { value: "16 Jets", label: "1HP Hydro Massage" },
  },
  {
    id: "led",
    icon: Sparkles,
    label: "LED Bubble",
    tagline: "Module 03 · Bubble Bath",
    title: "300W bubble bath with 16 chromotherapy LED jets",
    description:
      "A separate 300W air pump powers 16 colourful LED bubble jets for chromotherapy, controlled independently from the touch panel — a soft bubble bath with mood lighting, without engaging the high-pressure hydro massage.",
    bullets: [
      "300W dedicated air pump · whisper-quiet operation",
      "16 chromotherapy LED bubble jets · floor-mounted",
      "Independent control from the hydro massage circuit",
      "Soft bath / mood lighting mode for evening use",
    ],
    image: { src: "/images/bath/whirlpool-compact/led.jpg", alt: "REM7 Hydra Compact chromotherapy LED bubble jets" },
    metric: { value: "16 LEDs", label: "Chromotherapy Bath" },
  },
  {
    id: "apron",
    icon: Square,
    label: "Acrylic Apron",
    tagline: "Module 04 · Aesthetic",
    title: "2-side integral apron with 90° decorative acrylic",
    description:
      "A 2-side 1-pc integral apron with a 90° 8 mm decorative transparent acrylic accent simplifies finish detailing on site — engineered to drop into standard European bathroom corners and alcoves.",
    bullets: [
      "2-side 1-pc integral apron · clean alcove fit",
      "90° 8 mm decorative transparent acrylic accent",
      "No site cladding required for standard install",
      "Service access through the apron panel",
    ],
    image: { src: "/images/bath/whirlpool-compact/detail.jpg", alt: "REM7 Hydra Compact 90° decorative acrylic apron" },
    metric: { value: "8 mm", label: "Decorative Acrylic" },
  },
  {
    id: "plumbing",
    icon: Wrench,
    label: "Plumbing Pack",
    tagline: "Module 05 · Plumbing",
    title: "Complete EU plumbing package out-of-the-box",
    description:
      "Every REM7 Hydra Compact ships with a complete European plumbing pack — hot/cold mixer, diverter, hand shower, drainer with overflow — so the trade can install with standard rough-in and no exotic parts.",
    bullets: [
      "Hot / cold mixer + diverter mixer included",
      "Hand shower with hose · brushed chrome",
      "Backwater drainer with integrated overflow",
      "Pre-levelled, height-adjustable stainless feet",
    ],
    image: { src: "/images/bath/whirlpool-compact/lifestyle.jpg", alt: "REM7 Hydra Compact plumbing pack" },
    metric: { value: "EU-Ready", label: "Standard Rough-In" },
  },
  {
    id: "build",
    icon: ShieldCheck,
    label: "Build Quality",
    tagline: "Module 06 · Materials",
    title: "Sanitary-grade PMMA acrylic on stainless frame",
    description:
      "100% pure sanitary-grade PMMA acrylic shell — high-gloss, scratch / stain / fade resistant — reinforced with alkali-free fibreglass, resin and structural wood, mounted on a stainless steel frame with pre-levelled feet and CE / SGS / cUPC certification.",
    bullets: [
      "100% sanitary-grade PMMA acrylic shell",
      "Fibreglass + resin + wood reinforcement layers",
      "Stainless steel structural support frame",
      "CE · SGS · cUPC · ISO 9001 certified",
    ],
    image: { src: "/images/bath/whirlpool-compact/side.jpg", alt: "REM7 Hydra Compact acrylic shell side profile" },
    metric: { value: "5-Year", label: "Shell Warranty" },
  },
];

const Rem7WhirlpoolCompact = () => (
  <BathProductPage
    config={{
      ...config,
      specsPdfButton: (
        <Rem7HydraSpecPDFButton specs={config.specs} features={config.quickFeatures ?? []} />
      ),
      featureShowcase: (
        <Rem7HydraFeatureShowcase shortName={config.shortName} modules={featureModules} />
      ),
      specsPanel: (
        <Rem7HydraSpecsPanel
          shortName={config.shortName}
          productName={config.productName}
          sku={config.sku}
          heroImage={{
            src: "/images/bath/whirlpool-compact/angle.jpg",
            alt: "REM7 Hydra Compact whirlpool dimensional reference view",
          }}
          dimensions={dimensionMetrics}
          groups={specGroups}
          inBox={inBoxItems}
          certifications={["CE", "SGS", "cUPC", "ISO 9001"]}
          pdfButton={
            <Rem7HydraSpecPDFButton specs={config.specs} features={config.quickFeatures ?? []} />
          }
        />
      ),
    }}
  />
);
export default Rem7WhirlpoolCompact;
